import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import ProductCard from "@/components/ProductCard";
import { Baby, Pencil, Activity, Sparkles, Loader2, Gift, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { bySex } from "@/lib/bySex";

export default function BornDashboard({ onOpenProfile }) {
  const { profile, addToWishlist, inWishlist } = useProfile();
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;
    setLoading(true);
    api.get("/catalog/recommended-timeline", { params: { profile_id: profile.id } })
      .then(({ data }) => setBuckets(data.buckets || []))
      .catch(() => setBuckets([]))
      .finally(() => setLoading(false));
  }, [profile]);

  const dd = profile?.due_date;
  let months = 0;
  if (dd) {
    const born = new Date(dd.year, dd.month - 1, dd.day);
    const now = new Date();
    months = Math.max(0, (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth()));
  }
  const fem = profile?.sex === "femmina";
  const who = profile?.baby_name || bySex(profile?.sex, { femmina: "La tua bimba", maschio: "Il tuo bimbo", other: "Il tuo bimbo" });
  const ageLabel = months <= 0 ? `è appena ${fem ? "nata" : "nato"}` : `ha ${months} mes${months === 1 ? "e" : "i"}`;
  const kid = bySex(profile?.sex, { femmina: "della tua bimba", maschio: "del tuo bimbo", other: "del tuo bimbo" });
  const firstName = profile?.name?.split(" ")[0] || "";

  return (
    <section id="consigli" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-8 pb-4 scroll-mt-20" data-testid="born-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl bg-gradient-to-br from-[#F9ECE9] to-[#EAF0EC] border border-[#EAE3DC] p-6 sm:p-8"
      >
        <div className="flex items-center gap-2 text-[#C87D75] text-sm font-display">
          <Baby size={18} /> {who} {ageLabel}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2825] mt-2 leading-tight">
          Ciao, {firstName} 🤍
        </h1>
        <p className="text-[#5C534E] mt-2 max-w-xl">
          Tieni aggiornati i dati {kid}: ti mostriamo subito i capi giusti per la sua taglia
          e per la stagione, con il tuo sconto speciale da <strong>famiglia Minime</strong>.
        </p>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-5">
          <button data-testid="born-edit-data" onClick={onOpenProfile}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] px-5 py-2.5 text-sm transition-colors">
            <Pencil size={16} /> Modifica i dati del bimbo
          </button>
          <button data-testid="born-update-growth" onClick={onOpenProfile}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#4E7363] text-[#4E7363] hover:bg-white px-5 py-2.5 text-sm transition-colors">
            <Activity size={16} /> Aggiorna la crescita
          </button>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 text-[#C87D75] px-5 py-2.5 text-sm">
            <Gift size={16} /> Il tuo sconto personale del 15% si applica in automatico al checkout
          </span>
        </div>
      </motion.div>

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-[#C87D75]" />
          <h2 className="font-serif text-2xl text-[#2D2825]">Cosa comprare, mese per mese</h2>
        </div>
        <p className="text-sm text-[#8C7F78] mb-6">
          In base alla crescita {kid}, alla stagione e al clima di {profile?.city}: cosa va bene adesso e cosa preparare per i prossimi mesi.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#8C7F78] py-12 justify-center" data-testid="born-loading">
            <Loader2 className="animate-spin" size={18} /> Sto preparando i consigli…
          </div>
        ) : buckets.length === 0 ? (
          <p className="text-sm text-[#8C7F78] py-8" data-testid="born-empty">
            Nessun consiglio disponibile al momento. Riprova più tardi.
          </p>
        ) : (
          <div className="space-y-8" data-testid="born-timeline">
            {buckets.map((bk, bi) => (
              <div key={bk.offset} data-testid={`born-bucket-${bk.offset}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2D2825] text-white text-sm px-4 py-1.5">
                    {bi === 0 ? <Sparkles size={14} /> : <Clock size={14} />} {bk.label}
                  </span>
                  <span className="rounded-full bg-[#EAF0EC] text-[#4E7363] text-xs px-3 py-1">{bk.season}{bk.avg_temp != null ? ` · ~${bk.avg_temp}°C` : ""}</span>
                  <span className="rounded-full bg-[#F9ECE9] text-[#C87D75] text-xs px-3 py-1">Taglia {bk.size}</span>
                </div>
                <p className="text-sm text-[#5C534E] mb-4">{bk.note}</p>
                {bk.products.length === 0 ? (
                  <p className="text-xs text-[#8C7F78]">Nessun capo adatto disponibile per questo periodo.</p>
                ) : (
                  <div className="mb-rail sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
                    {bk.products.map((p, i) => (
                      <ProductCard key={p.id} product={p} delay={i * 0.04}
                        onAdd={() => addToWishlist(p)} inList={inWishlist(p.id)}
                        testid={`born-product-${bk.offset}-${p.id}`} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
